<template>
  <div class="exercises-sequences">
    <div class="exercises-folders" v-if="!readonly">
      <h5>Folders</h5>
      <v-btn
        rounded
        class="mr-2 mb-2 new"
        :color="!queryParams.folderId ? 'accent' : ''"
        elevation="0"
        :class="mouseDown ? 'dragging' : ''"
        @click="filterFolder(null)"
        @mouseup="folderMouseUp(null)"
        @dragenter.native="$event.preventDefault()"
        @dragover.native="allowDrop"
        @dragleave.native="leaveDropZone"
        @drop.native="
          leaveDropZone($event)
          folderMouseUp(null)
        "
      >
        Main
      </v-btn>
      <div
        class="exercises-folders--folder mr-2 pr-2 mb-2"
        v-for="folder in sortedFolders"
        :key="'folder-' + folder.id"
      >
        <v-btn
          rounded
          class="mr-2"
          elevation="0"
          :class="mouseDown ? 'dragging' : ''"
          :color="folder.id == queryParams.folderId ? 'accent' : 'bhWhite'"
          @click="filterFolder(folder.id)"
          @mouseup="folderMouseUp(folder.id)"
          @dragenter.native="$event.preventDefault()"
          @dragover.native="allowDrop"
          @dragleave.native="leaveDropZone"
          @drop.native="
            leaveDropZone($event)
            folderMouseUp(folder.id)
          "
        >
          {{ folder.title }}
        </v-btn>
        <v-btn
          icon
          elevation="0"
          @click.stop="openMenu($event, folder.id)"
          x-small
        >
          <v-icon>bhi-dots-vertical</v-icon>
        </v-btn>
        <v-menu
          rounded
          absolute
          offset-x
          transition="false"
          v-model="showMenu[folder.id]"
          :position-x="x"
          :position-y="y"
        >
          <v-list class="library-folder__more--menu">
            <v-list-item
              v-if="folder && !folder.locked"
              link
              dense
              @click="$emit('rename-folder', folder.id, folder.title)"
            >
              <v-list-item-icon>
                <v-icon>bhi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  Rename
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="folder && !folder.locked"
              link
              dense
              @click="removeFolder(folder)"
            >
              <v-list-item-icon>
                <v-icon>bhi-trash</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  Remove
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-btn
        v-if="sequenceFolders.length > 0"
        rounded
        class="exercises-folders--folder__add"
        text
        icon
        small
        @click="$emit('new-folder')"
      >
        <v-icon size="12">bhi-plus</v-icon>
      </v-btn>
      <v-btn
        v-else
        class="exercises-folders--folder__new"
        rounded
        text
        color="bhGray"
        @click="$emit('new-folder')"
      >
        <v-icon size="12">bhi-plus</v-icon> Create new folder
      </v-btn>
    </div>
    <h5 class="exercises-heading">Routines</h5>
    <div class="exercises-sequences__wrapper">
      <div
        class="exercises-sequences__wrapper__tile bordered"
        v-if="!queryParams.q && !readonly"
      >
        <h5>Create your own or import shared Routine</h5>
        <v-btn
          elevation="0"
          rounded
          color="accent"
          @click="$emit('create-new')"
        >
          Create new
        </v-btn>
        <v-btn
          v-if="!isAdmin"
          text
          color="accent"
          elevation="0"
          rounded
          @click="$emit('open-import')"
        >
          Browse shared Routines
        </v-btn>
      </div>
      <VueSelecto
        v-if="massSelect"
        :container="'document.body'"
        :dragContainer="'.exercises-sequences__wrapper'"
        :selectableTargets="['.sequences-tile__wrapper']"
        :selectByClick="false"
        :selectFromInside="false"
        :continueSelect="false"
        :toggleContinueSelect="'shift'"
        :hitRate="0"
        @select="onSelect"
      />
      <sequence-tile
        v-for="sequence in sequenceDefinitions"
        :draggable="sequence.selected"
        :key="'sequence-' + sequence.id"
        :id="sequence.id"
        :title="sequence.title"
        :dayCount="sequence.dayCount"
        :exerciseCount="sequence.itemsCount"
        :participants="sequence.participants ? sequence.participants.items : []"
        :assignable="!isAdmin"
        :sharable="sharable"
        :draft="sequence.draft"
        :collection-is-owned="sequence.owned"
        :collection-mode="collectionMode"
        :readonly="readonly"
        @share="openShareSequenceDialog(sequence)"
        @assigned="openAssignedDialog(sequence)"
        @mousedown.native="mouseDown = true"
        @mouseup.native="mouseDown = false"
        @click.native="$emit('edit', sequence.id)"
        v-on="$listeners"
      />
    </div>
    <v-pagination
      v-if="pagination && pagination.pageCount > 1"
      circle
      :length="pagination.pageCount"
      :value="pagination.page + 1"
      @input="$emit('pagination-change', $event)"
    />
  </div>
</template>

<script>
import SequenceTile from '@/components/exercises/SequenceTile.vue'
import VueSelecto from 'vue-selecto'

export default {
  name: 'ExercisesSequences',
  components: {
    SequenceTile,
    VueSelecto,
  },
  props: {
    sequenceDefinitions: {
      type: Array,
      default: () => [],
    },
    sequenceFolders: {
      type: Array,
      default: () => [],
    },
    queryParams: {
      type: Object,
      default: () => {},
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    sharable: {
      type: Boolean,
      default: false,
    },
    collectionMode: {
      type: Boolean,
      default: false,
    },
    massSelect: {
      type: Boolean,
      default: false,
    },
    advancedSearchActive: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: [Boolean, Object],
      default: false,
    },
  },
  data() {
    return {
      mouseDown: false,
      selectedSequences: [],
      showMenu: [],
      x: 0,
      y: 0,
    }
  },
  computed: {
    sortedFolders() {
      const folders = [...this.sequenceFolders]
      return this.sequenceFolders?.length > 0
        ? folders.sort((a, b) => a.title.localeCompare(b.title))
        : []
    },
  },
  created() {},
  methods: {
    removeFolder(folder) {
      if (!folder?.locked) {
        this.$emit('remove-folder', folder.id, 'sequence')
      }
    },
    openShareSequenceDialog(sequence) {
      this.$emit('share-dialog', sequence.id)
    },
    openAssignedDialog(sequence) {
      this.$emit('assigned-dialog', sequence.id)
    },
    filterFolder(value) {
      const key = 'folderId'
      this.$emit('search-on:input', { key, value })
    },
    onSelect(e) {
      e.added.forEach(el => {
        if (
          this.selectedSequences.indexOf(el.attributes['data-id'].nodeValue) ==
          -1
        )
          this.selectedSequences.push(el.attributes['data-id'].nodeValue)
        let item = this.sequenceDefinitions.find(item => {
          if (item.id == el.attributes['data-id'].nodeValue) return item
        })
        if (item) item.selected = true
        el.classList.add('selected')
      })
      e.removed.forEach(el => {
        let ind = this.selectedSequences.indexOf(
          el.attributes['data-id'].nodeValue,
        )
        let item = this.sequenceDefinitions.find(item => {
          if (item.id == el.attributes['data-id'].nodeValue) return item
        })
        if (item) item.selected = false
        if (ind > -1) this.selectedSequences.splice(ind, 1)
        el.classList.remove('selected')
      })
    },
    folderMouseUp(folderId) {
      if (this.mouseDown && this.selectedSequences.length > 0) {
        this.$emit('move-sequences', this.selectedSequences, folderId)
        this.selectedSequences = []
      }
      this.mouseDown = false
    },
    openMenu(e, id) {
      e.stopPropagation()
      this.x = e.clientX
      this.y = e.clientY
      this.$emit('changeMenuType', 'more')
      this.$nextTick(() => {
        this.showMenu = []
        this.showMenu[id] = true
      })
    },
    allowDrop(e) {
      e.target.classList.add('drop-active')
      e.preventDefault()
    },
    leaveDropZone(e) {
      e.target.classList.remove('drop-active')
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
.exercises-folders {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  h5 {
    color: $bh-gray;
    width: 100%;
    margin-bottom: 8px;
    font-size: 14px;
    margin-right: 8px;
  }
  &--folder__new {
    background-color: $bh-white;
    color: $bh-gray !important;
    margin-bottom: 8px;
    .v-icon {
      margin-right: 4px;
      margin-bottom: 2px;
    }
  }
  &--folder__add {
    margin-bottom: 8px;
  }
}
.exercises-sequences__wrapper__tile {
  width: 286px;
  height: 216px;
  border: 1px solid $bh-white;
  border-radius: 16px;
  padding: $gutter;
  margin: 8px;
  background: $bh-white;
  position: relative;
  &.bordered {
    border-color: transparent;
    background: transparent;
  }
  .v-btn {
    margin-bottom: 5px;
  }
  h5 {
    color: $bh-black;
  }
}
</style>
